/**
 * Wzór F: the print view's content split into A4 pages, from the measured height of each block. Done here (not by
 * the browser's own page breaks) so every page has its slim header, its footer and its page number, and so Safari
 * prints exactly what the screen shows. A row is never cut, a section head never stays alone at the bottom (it
 * goes with its first row), a section going over gets its head again („cd.”), the totals block stays whole.
 * Shared (2026-10-31): the server's PDF (modules/quotes `pdf-f.ts`) pages its blocks with the same rules.
 */

export type Block = { key: string; kind: 'hero' | 'head' | 'row' | 'totals'; height: number; section?: number };
export type PageBlock = Block & { continued?: true };

/** `first`: the content height of page 1; `next`: of the pages after it (they have the slim header). */
export function paginate(blocks: readonly Block[], first: number, next: number): PageBlock[][] {
  const heads = new Map(blocks.filter((b) => b.kind === 'head').map((b) => [b.section, b]));
  const pages: PageBlock[][] = [[]];
  let used = 0;
  const cap = () => (pages.length === 1 ? first : next);
  const newPage = () => {
    pages.push([]);
    used = 0;
  };
  const put = (b: PageBlock) => {
    pages[pages.length - 1]!.push(b);
    used += b.height;
  };
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!;
    const empty = pages[pages.length - 1]!.length === 0;
    if (b.kind === 'head') {
      const following = blocks[i + 1];
      const together = b.height + (following?.kind === 'row' && following.section === b.section ? following.height : 0);
      if (!empty && used + together > cap()) newPage();
      put(b);
      continue;
    }
    const page = pages[pages.length - 1]!;
    const afterItsHead = b.kind === 'row' && page.at(-1)?.kind === 'head' && page.at(-1)?.section === b.section;
    if (!empty && !afterItsHead && used + b.height > cap()) newPage();
    // a row opening a page under its section: the head again
    if (b.kind === 'row' && pages[pages.length - 1]!.length === 0) {
      const h = heads.get(b.section);
      if (h) put({ ...h, continued: true });
    }
    put(b);
  }
  return pages.filter((p) => p.length > 0);
}
